import type { GeneratedSchemaField, GeneratedSchemaKind } from '@cli-types/core-engine/add-field.js'
import type { PromptFieldOptions, SchemaPromptContext } from '@cli-types/next-adapter/commands.js'
import { buildLeafField } from '@core-engine/schema/add-field/build-leaf-field.js'
import { applyAdvancedOptions } from './apply-advanced-options.js'
import { applyDerivedFieldDefaults } from './apply-derived-field-defaults.js'
import { deriveAvailableFieldName } from './derive-available-field-name.js'
import { promptConfirm } from './prompt-confirm.js'
import { promptFieldName } from './prompt-field-name.js'
import { promptFieldOptions } from './prompt-field-options.js'
import { promptFieldType } from './prompt-field-type.js'
import { promptFormContainerField } from './prompt-form-container-field.js'
import { promptFormFileOptions } from './prompt-form-file-options.js'
import { promptMultiple } from './prompt-multiple.js'
import { promptRelationshipTarget } from './prompt-relationship-target.js'
import { promptSchemaContainerField } from './prompt-schema-container-field.js'
import { promptTabsField } from './prompt-tabs-field.js'
import { promptText } from './prompt-text.js'
import { shouldPromptRequired } from './should-prompt-required.js'

export async function promptField(
  context: SchemaPromptContext,
  kind: GeneratedSchemaKind,
  schemasDir: string,
  scope: 'schema' | 'list-item',
  depth = 0,
  promptOptions: PromptFieldOptions = {}
): Promise<GeneratedSchemaField> {
  const type = promptOptions.typeFirst ? await promptFieldType(context, kind) : null
  const label = await promptText(context, depth === 0 ? 'Field label' : 'Child field label')
  const name = promptOptions.deriveName
    ? deriveAvailableFieldName(label, promptOptions.existingNames)
    : await promptFieldName(context, label)
  const fieldType = type ?? (await promptFieldType(context, kind))

  if (kind !== 'form' && ['group', 'section', 'list'].includes(fieldType)) {
    return promptSchemaContainerField(
      context,
      kind,
      schemasDir,
      scope,
      name,
      label,
      fieldType,
      depth,
      promptOptions
    )
  }

  if (kind !== 'form' && fieldType === 'tabs') {
    return promptTabsField(context, kind, schemasDir, scope, name, label, depth, promptOptions)
  }

  if (kind === 'form' && ['group', 'list'].includes(fieldType)) {
    return promptFormContainerField(
      context,
      kind,
      schemasDir,
      scope,
      name,
      label,
      fieldType,
      depth,
      promptOptions
    )
  }

  const useFormFileOptionsPrompt =
    (await shouldPromptRequired(fieldType)) && kind === 'form' && fieldType === 'file'
  const fileOptions = useFormFileOptionsPrompt
    ? await promptFormFileOptions(context)
    : { required: false, multiple: false }
  const required = useFormFileOptionsPrompt
    ? fileOptions.required
    : (await shouldPromptRequired(fieldType))
      ? await promptConfirm(context, 'Required field?', false)
      : false
  const creatable =
    kind !== 'form' && fieldType === 'select'
      ? await promptConfirm(context, 'Allow editors to create options?', false)
      : false
  const options = await promptFieldOptions(context, kind, fieldType, creatable)
  const multiple = useFormFileOptionsPrompt
    ? fileOptions.multiple
    : await promptMultiple(context, kind, fieldType)
  const relationship = await promptRelationshipTarget(context, kind, fieldType, schemasDir)

  const field = buildLeafField({
    kind,
    type: fieldType,
    name,
    label,
    required,
    multiple,
    creatable,
    options,
    relationship
  })

  applyDerivedFieldDefaults(field, fieldType, promptOptions)
  await applyAdvancedOptions(context, field, kind, fieldType, promptOptions)
  return field
}
